import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import type { TFunction } from 'i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSelect } from '../components/LanguageSelect';
import {
  getJSON,
  patchJSON,
  postJSON,
  postLogout,
  userWalletApiBase,
  type MessageResponse,
  type UserProfile,
  type WalletsResponse,
} from '../lib/api';
import { loadMpcWasm, type ImportPrepareOut, type KeygenPrepareOut } from '../lib/mpcWasm';
import { copyTextToClipboard } from '../lib/copyToClipboard';
import { encryptBytesWithPaymentPassword } from '../lib/paymentCrypto';
import { sha256Utf8Hex } from '../lib/passwordWire';
import { appMainClass, narrowPanelError, narrowPanelNeutral, pageWrapClass } from '../layout/page';
import {
  inputMonoStackFieldClass,
  inputStackFieldClass,
  outlineButtonBlockClass,
  outlineSoftButtonClass,
  primaryButtonBlockClass,
  primaryButtonProminentClass,
} from '../components/form/fieldClasses';

type KeygenSessionResponse = {
  session_id: string;
  p2_hex: string;
  joint_pubkey_hex: string;
};

type CreateMode = 'new' | 'import';

const PAILLIER_BITS = 2048;

const SHOW_HIDDEN_WALLETS_KEY = 'cointrader_show_hidden_wallets';

function sessStorageKey(walletId: number): string {
  return `cointrader_mpc_sess:${walletId}`;
}

function mpcBlobStorageKey(walletId: number): string {
  return `cointrader_mpc_blob:${walletId}`;
}

function IconCopy({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </svg>
  );
}

function IconCheck({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

type CreateWalletFormProps = {
  prominent: boolean;
  profile: Pick<UserProfile, 'mpc_enabled' | 'has_payment_password' | 'mfa_enabled'>;
  t: TFunction;
  createMode: CreateMode;
  setCreateMode: (m: CreateMode) => void;
  createName: string;
  setCreateName: (v: string) => void;
  createPwd: string;
  setCreatePwd: (v: string) => void;
  createMfa: string;
  setCreateMfa: (v: string) => void;
  importKey: string;
  setImportKey: (v: string) => void;
  busy: boolean;
  onSubmit: (e: FormEvent) => void;
};

/** Module-level component so React does not remount inputs on every parent render. */
function CreateWalletForm({
  prominent,
  profile,
  t,
  createMode,
  setCreateMode,
  createName,
  setCreateName,
  createPwd,
  setCreatePwd,
  createMfa,
  setCreateMfa,
  importKey,
  setImportKey,
  busy,
  onSubmit,
}: CreateWalletFormProps) {
  if (!profile.mpc_enabled) {
    return null;
  }
  const ring =
    'rounded-2xl border border-blue-200 bg-gradient-to-b from-blue-50/80 to-white p-6 shadow-md';
  const inner = (
    <>
      {prominent ? (
        <>
          <h2 className="text-xl font-semibold text-slate-900">{t('home.createWalletTitle')}</h2>
          <p className="mt-1 text-sm text-slate-600">{t('home.createWalletHint')}</p>
        </>
      ) : null}
      {!profile.has_payment_password ? (
        <p className="mt-3 text-sm text-amber-800">
          {t('home.needPaymentGoSettings')}{' '}
          <Link to="/home/settings" className="font-medium text-blue-700 underline hover:text-blue-900">
            {t('home.openSettings')}
          </Link>
        </p>
      ) : null}
      <form className={`grid gap-4 ${prominent ? 'mt-4' : 'mt-0'}`} onSubmit={(e) => void onSubmit(e)}>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setCreateMode('new')}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              createMode === 'new' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {t('home.createModeNew')}
          </button>
          <button
            type="button"
            onClick={() => setCreateMode('import')}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              createMode === 'import' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {t('home.createModeImport')}
          </button>
        </div>
        {createMode === 'import' ? (
          <input
            required
            type="password"
            autoComplete="off"
            className={inputMonoStackFieldClass}
            placeholder={t('home.importKeyPh')}
            value={importKey}
            onChange={(e) => setImportKey(e.target.value)}
          />
        ) : null}
        <input
          required
          className={inputStackFieldClass}
          placeholder={t('home.createWalletNamePh')}
          value={createName}
          onChange={(e) => setCreateName(e.target.value)}
          autoComplete="off"
        />
        <input
          required
          type="password"
          autoComplete="current-password"
          className={inputStackFieldClass}
          placeholder={t('home.createWalletPayPh')}
          value={createPwd}
          onChange={(e) => setCreatePwd(e.target.value)}
        />
        {profile.mfa_enabled ? (
          <input
            required
            inputMode="numeric"
            autoComplete="one-time-code"
            className={inputStackFieldClass}
            placeholder={t('mfa.code')}
            value={createMfa}
            onChange={(e) => setCreateMfa(e.target.value)}
          />
        ) : null}
        <button
          type="submit"
          disabled={busy || !profile.has_payment_password}
          className={prominent ? primaryButtonProminentClass : primaryButtonBlockClass}
        >
          {busy ? t('home.creatingWallet') : t('home.createWalletSubmit')}
        </button>
      </form>
    </>
  );
  if (prominent) {
    return <section className={ring}>{inner}</section>;
  }
  return <div className="space-y-4">{inner}</div>;
}

export default function UserHomePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [wallets, setWallets] = useState<WalletsResponse['wallets']>([]);
  const [msg, setMsg] = useState('');
  const [msgOk, setMsgOk] = useState(false);
  const [busy, setBusy] = useState(false);

  const [stubName, setStubName] = useState('');
  const [stubPwd, setStubPwd] = useState('');
  const [stubMfa, setStubMfa] = useState('');

  const [createMode, setCreateMode] = useState<CreateMode>('new');
  const [createName, setCreateName] = useState('');
  const [createPwd, setCreatePwd] = useState('');
  const [createMfa, setCreateMfa] = useState('');
  const [importKey, setImportKey] = useState('');
  const [copiedWalletId, setCopiedWalletId] = useState<number | null>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showHiddenWallets, setShowHiddenWallets] = useState(() => {
    try {
      return typeof localStorage !== 'undefined' && localStorage.getItem(SHOW_HIDDEN_WALLETS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [hiddenBusyId, setHiddenBusyId] = useState<number | null>(null);
  const [orderBusyId, setOrderBusyId] = useState<number | null>(null);
  const [actionMenuWalletId, setActionMenuWalletId] = useState<number | null>(null);
  const walletMenuRootsRef = useRef(new Map<number, HTMLDivElement>());

  useEffect(() => {
    try {
      localStorage.setItem(SHOW_HIDDEN_WALLETS_KEY, showHiddenWallets ? '1' : '0');
    } catch {
      /* ignore quota / private mode */
    }
  }, [showHiddenWallets]);

  useEffect(() => {
    if (actionMenuWalletId === null) {
      return;
    }
    const openId = actionMenuWalletId;
    const onPointerDown = (e: PointerEvent) => {
      const root = walletMenuRootsRef.current.get(openId);
      const t = e.target;
      if (t instanceof Node && root?.contains(t)) {
        return;
      }
      setActionMenuWalletId(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [actionMenuWalletId]);

  const copyEvmAddress = useCallback(
    async (walletId: number, text: string) => {
      const ok = await copyTextToClipboard(text);
      if (!ok) {
        setMsgOk(false);
        setMsg(t('home.copyFailed'));
        return;
      }
      setMsg((prev) => (prev === t('home.copyFailed') ? '' : prev));
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      setCopiedWalletId(walletId);
      copyTimeoutRef.current = setTimeout(() => {
        setCopiedWalletId((cur) => (cur === walletId ? null : cur));
        copyTimeoutRef.current = null;
      }, 2500);
    },
    [t],
  );

  async function setWalletHidden(walletId: number, hidden: boolean) {
    setHiddenBusyId(walletId);
    setMsg('');
    try {
      const { res, data } = await patchJSON<MessageResponse>(`${userWalletApiBase(walletId)}/hidden`, { hidden });
      if (!res.ok) {
        setMsgOk(false);
        setMsg(data.error || t('common.error'));
        return;
      }
      setWallets((prev) => {
        const next = prev.map((x) => (x.id === walletId ? { ...x, hidden } : x));
        if (showHiddenWallets) {
          return next;
        }
        return next.filter((x) => !x.hidden);
      });
      setActionMenuWalletId(null);
    } finally {
      setHiddenBusyId(null);
    }
  }

  async function moveWallet(walletId: number, position: 'top' | 'bottom') {
    setOrderBusyId(walletId);
    setMsg('');
    try {
      const { res, data } = await patchJSON<MessageResponse>(`${userWalletApiBase(walletId)}/order`, { position });
      if (!res.ok) {
        setMsgOk(false);
        setMsg(data.error || t('common.error'));
        return;
      }
      await load({ silent: true });
      setActionMenuWalletId(null);
    } finally {
      setOrderBusyId(null);
    }
  }

  useEffect(
    () => () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    },
    [],
  );

  const load = useCallback(
    async (opts?: { silent?: boolean }): Promise<WalletsResponse['wallets'] | null> => {
      const silent = opts?.silent === true;
      if (!silent) {
        setLoading(true);
        setForbidden(false);
      }
      try {
        const { res, data } = await getJSON<UserProfile>('/api/user/profile');
        if (res.status === 401) {
          navigate('/', { replace: true });
          return null;
        }
        if (res.status === 403) {
          setForbidden(true);
          setProfile(null);
          return null;
        }
        if (!res.ok) {
          setMsg(data.error || t('common.error'));
          setMsgOk(false);
          setProfile(null);
          return null;
        }
        setProfile({
          username: data.username,
          has_payment_password: data.has_payment_password,
          mfa_enabled: data.mfa_enabled,
          mpc_stub: data.mpc_stub,
          mpc_enabled: data.mpc_enabled,
          mpc_server_repo_note: data.mpc_server_repo_note,
          mpc_client_repo_note: data.mpc_client_repo_note,
        });
        const wr = await getJSON<WalletsResponse>(`/api/user/wallets?include_hidden=${showHiddenWallets ? '1' : '0'}`);
        const list = wr.res.ok ? (wr.data.wallets ?? []) : [];
        if (wr.res.ok) {
          setWallets(list);
        }
        return list;
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [navigate, showHiddenWallets, t],
  );

  useEffect(() => {
    void load();
  }, [load, i18n.language]);

  async function onStubCreate(e: FormEvent) {
    e.preventDefault();
    setMsg('');
    setBusy(true);
    try {
      const stubBody: Record<string, unknown> = {
        name: stubName.trim(),
        payment_password: await sha256Utf8Hex(stubPwd.trim()),
      };
      if (profile?.mfa_enabled) {
        stubBody.mfa_code = stubMfa.trim();
      }
      const { res, data } = await postJSON<MessageResponse>('/api/user/wallets/stub', stubBody);
      if (!res.ok) {
        setMsgOk(false);
        setMsg(data.error || t('common.error'));
        return;
      }
      setMsgOk(true);
      setMsg(data.message || '');
      setStubName('');
      setStubPwd('');
      setStubMfa('');
      await load({ silent: true });
    } finally {
      setBusy(false);
    }
  }

  async function onCreateMpcWallet(e: FormEvent) {
    e.preventDefault();
    setMsg('');
    setBusy(true);
    const name = createName.trim();
    const pwd = createPwd;
    const mode = createMode;
    const sk = importKey.trim();
    try {
      if (!profile?.has_payment_password) {
        setMsgOk(false);
        setMsg(t('home.needPaymentGoSettings'));
        return;
      }
      if (!name) {
        setMsgOk(false);
        setMsg(t('home.walletNameRequired'));
        return;
      }
      if (mode === 'import' && !sk) {
        setMsgOk(false);
        setMsg(t('home.importKeyRequired'));
        return;
      }
      const wasm = await loadMpcWasm();
      let sessionJson: string;
      let kgResp: KeygenSessionResponse;
      let clientSecret: KeygenPrepareOut['client_secret'];
      let source: 'mpc' | 'import';

      if (mode === 'new') {
        const prep = JSON.parse(wasm.wasm_keygen_prepare(PAILLIER_BITS)) as KeygenPrepareOut;
        const { res, data } = await postJSON<KeygenSessionResponse & { error?: string }>(
          '/api/mpc/v1/keygen',
          prep.keygen_request,
        );
        if (!res.ok) {
          setMsgOk(false);
          setMsg(data.error || t('common.error'));
          return;
        }
        kgResp = data;
        sessionJson = wasm.wasm_keygen_finalize(
          JSON.stringify(kgResp),
          JSON.stringify(prep.client_secret),
        );
        clientSecret = prep.client_secret;
        source = 'mpc';
      } else {
        const prep = JSON.parse(wasm.wasm_import_prepare(sk, PAILLIER_BITS)) as ImportPrepareOut;
        const { res, data } = await postJSON<KeygenSessionResponse & { error?: string }>(
          '/api/mpc/v1/keygen/import',
          prep.import_request,
        );
        if (!res.ok) {
          setMsgOk(false);
          setMsg(data.error || t('common.error'));
          return;
        }
        kgResp = data;
        sessionJson = wasm.wasm_import_finalize(
          JSON.stringify(kgResp),
          JSON.stringify(prep.client_secret),
        );
        clientSecret = prep.client_secret;
        source = 'import';
      }

      const encSess = await encryptBytesWithPaymentPassword(pwd, new TextEncoder().encode(sessionJson));
      const encSec = await encryptBytesWithPaymentPassword(
        pwd,
        new TextEncoder().encode(JSON.stringify(clientSecret)),
      );
      const payWire = await sha256Utf8Hex(pwd);
      const mpcBody: Record<string, unknown> = {
        name,
        payment_password: payWire,
        session_id: kgResp.session_id,
        encrypted_client_session: encSess,
        encrypted_client_secret: encSec,
        source,
      };
      if (profile?.mfa_enabled) {
        mpcBody.mfa_code = createMfa.trim();
      }
      const { res: r2, data: d2 } = await postJSON<MessageResponse>('/api/user/wallets/mpc', mpcBody);
      if (!r2.ok) {
        setMsgOk(false);
        setMsg(d2.error || t('common.error'));
        return;
      }
      const list = await load({ silent: true });
      const created = list?.find((w) => w.name === name);
      if (created) {
        sessionStorage.setItem(
          mpcBlobStorageKey(created.id),
          JSON.stringify({
            wallet_id: created.id,
            encrypted_client_session: encSess,
            encrypted_client_secret: encSec,
          }),
        );
        sessionStorage.setItem(sessStorageKey(created.id), sessionJson);
      }
      setMsgOk(true);
      setMsg(t('home.walletCreatedFull', { name }));
      setCreateName('');
      setCreatePwd('');
      setCreateMfa('');
      setImportKey('');
    } catch (err) {
      setMsgOk(false);
      setMsg(String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onLogout() {
    await postLogout();
    navigate('/', { replace: true });
  }

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-slate-100 text-slate-600">{t('home.loading')}</div>
    );
  }

  if (forbidden) {
    return (
      <div className={pageWrapClass}>
        <main className={narrowPanelNeutral}>
          <p className="text-sm text-slate-700">{t('home.forbiddenAdmin')}</p>
          <button type="button" className={`mt-4 ${outlineButtonBlockClass}`} onClick={() => void onLogout()}>
            {t('user.logout')}
          </button>
        </main>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className={pageWrapClass}>
        <main className={narrowPanelError}>
          <p className="text-sm text-red-600">{msg || t('common.error')}</p>
        </main>
      </div>
    );
  }

  const hasWallets = wallets.length > 0;

  return (
    <div className={pageWrapClass}>
      <main className={appMainClass}>
        <header className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-500">{profile.username}</p>
          <div className="flex flex-wrap items-center gap-2">
            <LanguageSelect />
            <Link to="/home/swap" className={`inline-flex items-center ${outlineSoftButtonClass}`}>
              {t('swap.nav')}
            </Link>
            <Link to="/home/transfer" className={`inline-flex items-center ${outlineSoftButtonClass}`}>
              {t('transfer.nav')}
            </Link>
            <Link to="/home/proxy-transfer" className={`inline-flex items-center ${outlineSoftButtonClass}`}>
              {t('proxyTransfer.nav')}
            </Link>
            <Link to="/home/settings" className={`inline-flex items-center ${outlineSoftButtonClass}`}>
              {t('home.settingsLink')}
            </Link>
            <button type="button" className={outlineSoftButtonClass} onClick={() => void load({ silent: true })}>
              {t('home.refresh')}
            </button>
            <button type="button" className={outlineSoftButtonClass} onClick={() => void onLogout()}>
              {t('user.logout')}
            </button>
          </div>
        </header>

        {msg ? (
          <div
            role="status"
            className={`rounded-xl border px-4 py-3 text-sm font-medium ${
              msgOk
                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                : 'border-red-200 bg-red-50 text-red-800'
            }`}
          >
            {msg}
          </div>
        ) : null}

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{t('home.title')}</h1>
          <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-500">{t('home.walletListSubtitle')}</p>
            {hasWallets ? (
              <label className="flex cursor-pointer select-none items-center gap-2 text-sm text-slate-700 sm:shrink-0">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  checked={showHiddenWallets}
                  onChange={(e) => setShowHiddenWallets(e.target.checked)}
                />
                {t('home.showHiddenWallets')}
              </label>
            ) : null}
          </div>
          <div className="mt-5 divide-y divide-slate-100 rounded-xl border border-slate-100 bg-slate-50/50">
            {!hasWallets ? (
              <div className="px-4 py-10 text-center">
                <p className="text-slate-600">{t('home.walletListEmpty')}</p>
                {profile.mpc_enabled ? (
                  <p className="mt-2 text-sm text-slate-500">{t('home.walletEmptyHint')}</p>
                ) : (
                  <p className="mt-2 text-sm text-slate-500">{t('home.mpcDisabledHint')}</p>
                )}
              </div>
            ) : wallets.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <p className="text-slate-600">{t('home.walletAllHiddenInList')}</p>
                <p className="mt-2 text-sm text-slate-500">{t('home.walletAllHiddenHint')}</p>
              </div>
            ) : (
              <ul className="text-left">
                {wallets.map((w) => {
                  const addr = w.evm_address?.trim() ?? '';
                  const showAddr = addr.length > 0;
                  const rowMuted = w.hidden && showHiddenWallets;
                  return (
                    <li
                      key={w.id}
                      className={`flex flex-col gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0 sm:flex-row sm:items-start sm:justify-between sm:gap-4 ${
                        rowMuted ? 'bg-slate-100/60' : ''
                      }`}
                    >
                      <div className="flex min-w-0 flex-col gap-2 sm:max-w-[40%]">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <Link
                            to={`/home/wallet/${w.id}`}
                            className="shrink-0 font-medium text-blue-700 hover:text-blue-900 hover:underline"
                          >
                            {w.name}
                          </Link>
                          {w.hidden ? (
                            <span className="rounded-md bg-slate-200/90 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600">
                              {t('home.walletHiddenBadge')}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      {showAddr ? (
                        <div className="flex min-w-0 flex-1 items-start gap-2 sm:justify-end">
                          <span className="min-w-0 flex-1 break-all font-mono text-sm leading-relaxed text-slate-700">
                            {addr}
                          </span>
                          <div className="flex shrink-0 items-start gap-1 sm:items-start">
                            <button
                              type="button"
                              className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-200/80 hover:text-slate-800"
                              title={copiedWalletId === w.id ? t('home.evmAddressCopied') : t('home.copyEvmAddress')}
                              aria-label={copiedWalletId === w.id ? t('home.evmAddressCopied') : t('home.copyEvmAddress')}
                              onClick={() => void copyEvmAddress(w.id, addr)}
                            >
                              {copiedWalletId === w.id ? (
                                <IconCheck className="text-emerald-600" />
                              ) : (
                                <IconCopy />
                              )}
                            </button>
                            {copiedWalletId === w.id ? (
                              <span role="status" aria-live="polite" className="text-xs font-medium text-emerald-700">
                                {t('home.evmAddressCopied')}
                              </span>
                            ) : null}
                          </div>
                          <div
                            className="relative shrink-0"
                            ref={(el) => {
                              if (el) {
                                walletMenuRootsRef.current.set(w.id, el);
                              } else {
                                walletMenuRootsRef.current.delete(w.id);
                              }
                            }}
                          >
                            <button
                              type="button"
                              className="rounded-md px-2 py-1 text-lg leading-none text-slate-500 transition-colors hover:bg-slate-200/80 hover:text-slate-800"
                              aria-label={t('home.walletActions')}
                              title={t('home.walletActions')}
                              onClick={() => setActionMenuWalletId((cur) => (cur === w.id ? null : w.id))}
                            >
                              ...
                            </button>
                            {actionMenuWalletId === w.id ? (
                              <div className="absolute right-0 z-20 mt-1 min-w-44 rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
                                <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
                                  <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-slate-300 text-slate-700 focus:ring-slate-400"
                                    checked={!!w.hidden}
                                    disabled={hiddenBusyId === w.id}
                                    onChange={(e) => void setWalletHidden(w.id, e.target.checked)}
                                  />
                                  <span>{t('home.walletHideFromHomeList')}</span>
                                </label>
                                <button
                                  type="button"
                                  disabled={orderBusyId === w.id}
                                  onClick={() => void moveWallet(w.id, 'top')}
                                  className="mt-2 w-full rounded-md px-2 py-1 text-left text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                                >
                                  {t('home.walletMoveTop')}
                                </button>
                                <button
                                  type="button"
                                  disabled={orderBusyId === w.id}
                                  onClick={() => void moveWallet(w.id, 'bottom')}
                                  className="mt-1 w-full rounded-md px-2 py-1 text-left text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                                >
                                  {t('home.walletMoveBottom')}
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          <span className="text-sm text-slate-500 sm:text-right">{t('home.walletNoEvmAddr')}</span>
                          <div
                            className="relative shrink-0"
                            ref={(el) => {
                              if (el) {
                                walletMenuRootsRef.current.set(w.id, el);
                              } else {
                                walletMenuRootsRef.current.delete(w.id);
                              }
                            }}
                          >
                            <button
                              type="button"
                              className="rounded-md px-2 py-1 text-lg leading-none text-slate-500 transition-colors hover:bg-slate-200/80 hover:text-slate-800"
                              aria-label={t('home.walletActions')}
                              title={t('home.walletActions')}
                              onClick={() => setActionMenuWalletId((cur) => (cur === w.id ? null : w.id))}
                            >
                              ...
                            </button>
                            {actionMenuWalletId === w.id ? (
                              <div className="absolute right-0 z-20 mt-1 min-w-44 rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
                                <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
                                  <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-slate-300 text-slate-700 focus:ring-slate-400"
                                    checked={!!w.hidden}
                                    disabled={hiddenBusyId === w.id}
                                    onChange={(e) => void setWalletHidden(w.id, e.target.checked)}
                                  />
                                  <span>{t('home.walletHideFromHomeList')}</span>
                                </label>
                                <button
                                  type="button"
                                  disabled={orderBusyId === w.id}
                                  onClick={() => void moveWallet(w.id, 'top')}
                                  className="mt-2 w-full rounded-md px-2 py-1 text-left text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                                >
                                  {t('home.walletMoveTop')}
                                </button>
                                <button
                                  type="button"
                                  disabled={orderBusyId === w.id}
                                  onClick={() => void moveWallet(w.id, 'bottom')}
                                  className="mt-1 w-full rounded-md px-2 py-1 text-left text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                                >
                                  {t('home.walletMoveBottom')}
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>

        {!hasWallets && profile.mpc_enabled ? (
          <CreateWalletForm
            prominent
            profile={profile}
            t={t}
            createMode={createMode}
            setCreateMode={setCreateMode}
            createName={createName}
            setCreateName={setCreateName}
            createPwd={createPwd}
            setCreatePwd={setCreatePwd}
            createMfa={createMfa}
            setCreateMfa={setCreateMfa}
            importKey={importKey}
            setImportKey={setImportKey}
            busy={busy}
            onSubmit={onCreateMpcWallet}
          />
        ) : null}

        {!profile.mpc_enabled && !hasWallets ? (
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm text-slate-600">{t('home.mpcDisabledHint')}</p>
          </section>
        ) : null}

        {hasWallets && profile.mpc_enabled ? (
          <details className="group rounded-2xl border border-slate-200/80 bg-white shadow-sm">
            <summary className="cursor-pointer list-none px-5 py-4 text-sm font-medium text-slate-700 marker:content-none [&::-webkit-details-marker]:hidden">
              <span className="flex items-center justify-between gap-2">
                {t('home.addWalletFold')}
                <span className="text-slate-400 transition group-open:rotate-180">▼</span>
              </span>
            </summary>
            <div className="border-t border-slate-100 px-5 pb-5 pt-2">
              <CreateWalletForm
                prominent={false}
                profile={profile}
                t={t}
                createMode={createMode}
                setCreateMode={setCreateMode}
                createName={createName}
                setCreateName={setCreateName}
                createPwd={createPwd}
                setCreatePwd={setCreatePwd}
                createMfa={createMfa}
                setCreateMfa={setCreateMfa}
                importKey={importKey}
                setImportKey={setImportKey}
                busy={busy}
                onSubmit={onCreateMpcWallet}
              />
            </div>
          </details>
        ) : null}

        {profile.mpc_stub && (
          <section className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5 shadow-sm">
            {!profile.has_payment_password && (
              <p className="mb-2 text-sm text-amber-900">
                {t('home.needPaymentGoSettings')}{' '}
                <Link to="/home/settings" className="font-medium underline">
                  {t('home.openSettings')}
                </Link>
              </p>
            )}
            <h2 className="text-sm font-semibold text-amber-900">{t('home.stubSection')}</h2>
            <p className="mt-1 text-xs text-amber-800/90">{t('home.stubHint')}</p>
            <form className="mt-3 grid gap-2" onSubmit={(e) => void onStubCreate(e)}>
              <input
                required
                className={inputStackFieldClass}
                placeholder={t('home.stubName')}
                value={stubName}
                onChange={(e) => setStubName(e.target.value)}
              />
              <input
                required
                type="password"
                autoComplete="new-password"
                className={inputStackFieldClass}
                placeholder={t('home.createWalletPayPh')}
                value={stubPwd}
                onChange={(e) => setStubPwd(e.target.value)}
              />
              {profile.mfa_enabled ? (
                <input
                  required
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className={inputStackFieldClass}
                  placeholder={t('mfa.code')}
                  value={stubMfa}
                  onChange={(e) => setStubMfa(e.target.value)}
                />
              ) : null}
              <button
                type="submit"
                disabled={busy || !profile.has_payment_password}
                className="h-9 rounded-lg bg-amber-700 text-sm font-medium text-white hover:bg-amber-800 disabled:opacity-60"
              >
                {t('home.createStub')}
              </button>
            </form>
          </section>
        )}
      </main>
    </div>
  );
}
